import { NgModule, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { TranslateLoader, TranslateModule, TranslationObject } from '@ngx-translate/core';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { environment } from '../environments/environment';
import { Router } from '@angular/router';
import {
  HttpClient,
  HttpClientModule,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { providePrimeNG } from 'primeng/config';
import { MessageService } from 'primeng/api';
import { ekowitrynaPreset, provideEkoHostAppMetadata } from '@ekowitryna/ui';
import packageJson from '../../package.json';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  Configuration,
  provideApi
} from '@mobiletract/kobize-service-angular-client';
import {
  ApiModule as AuthServiceApiModule,
  Configuration as AuthServiceConfiguration,
  ConfigurationParameters as AuthServiceConfigurationParameters
} from '@mobiletract/auth-service-angular-client';
import {
  ApiModule as AccountServiceApiModule,
  Configuration as AccountServiceConfiguration,
  ConfigurationParameters as AccountServiceConfigurationParameters
} from '@mobiletract/account-service-angular-client';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import {
  EkoLayoutAppBarComponent,
  EkoLayoutSidebarComponent,
  EkoNavigationMenuComponent,
  EkoScrollToTopComponent,
} from '@ekowitryna/ui';
import { CompanyContextPanelComponent } from './features/company-context/company-context-panel.component';
import { FirstConfigurationComponent } from './features/first-configuration/first-configuration.component';
import { ProfileRedirectComponent } from './profile-redirect.component';
import { provideEkoPreferencesSync } from './core/providers/eko-dark-mode.providers';
import { provideEkoBrowserTitle } from '@ekowitryna/ui';

const KOBIZE_API_BASE_PATH = `${environment.apiUrl}/kobize`;

export function initializeKeycloak(keycloak: KeycloakService, router: Router) {
  return async () => {
    const timeoutMs = 10000;

    try {
      const initPromise = keycloak.init({
        config: {
          url: environment.keycloakUrl,
          realm: environment.realm,
          clientId: environment.clientId
        },
        initOptions: {
          onLoad: 'check-sso',
          checkLoginIframe: false,
          silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html'
        },
        enableBearerInterceptor: true,
        bearerExcludedUrls: ['/assets', '/i18n']
      });

      // Do not block Angular bootstrap forever if auth provider is unreachable.
      await Promise.race([
        initPromise,
        new Promise((_, reject) => {
          setTimeout(() => reject(new Error(`Keycloak init timeout after ${timeoutMs}ms`)), timeoutMs);
        })
      ]);
    } catch (error) {
      console.error('Keycloak initialization failed', error);
    }
  };
}

export function createTranslateLoader(http: HttpClient): TranslateLoader {
  return {
    getTranslation: (lang: string): Observable<TranslationObject> =>
      http.get<TranslationObject>(`./assets/i18n/${lang}.json`)
  };
}

export function kobizeApiConfigFactory(keycloak: KeycloakService): Configuration {
  return new Configuration({
    basePath: KOBIZE_API_BASE_PATH,
    credentials: {
      'bearer-jwt': () => keycloak.getKeycloakInstance()?.token ?? ''
    }
  });
}

export function authApiConfigFactory(): AuthServiceConfiguration {
  const params: AuthServiceConfigurationParameters = {
    basePath: environment.authApiUrl,
  };
  return new AuthServiceConfiguration(params);
}

export function accountApiConfigFactory(): AccountServiceConfiguration {
  const params: AccountServiceConfigurationParameters = {
    basePath: environment.accountsApiUrl,
  };
  return new AccountServiceConfiguration(params);
}

@NgModule({
  declarations: [
    AppComponent,
    CompanyContextPanelComponent,
    FirstConfigurationComponent,
    ProfileRedirectComponent
  ],
  imports: [
    BrowserModule,
    CommonModule,
    TranslateModule.forRoot({
      defaultLanguage: 'pl',
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient]
      }
    }),
    AppRoutingModule,
    KeycloakAngularModule,
    AuthServiceApiModule.forRoot(authApiConfigFactory),
    AccountServiceApiModule.forRoot(accountApiConfigFactory),
    HttpClientModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    SelectModule,
    SkeletonModule,
    TooltipModule,
    EkoLayoutAppBarComponent,
    EkoLayoutSidebarComponent,
    EkoNavigationMenuComponent,
    EkoScrollToTopComponent
  ],
  bootstrap: [AppComponent],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      deps: [KeycloakService, Router],
      multi: true
    },
    KeycloakService,
    MessageService,
    provideEkoHostAppMetadata({
      name: packageJson.name,
      version: packageJson.version
    }),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: ekowitrynaPreset,
        options: {
          darkModeSelector: '.dark',
          /* Bez warstwy – style PrimeNG mają pierwszeństwo nad Tailwind (który może być unlayered) */
          cssLayer: false
        }
      }
    }),
    provideApi(KOBIZE_API_BASE_PATH),
    {
      provide: Configuration,
      useFactory: kobizeApiConfigFactory,
      deps: [KeycloakService]
    },
    ...provideEkoPreferencesSync(),
    provideEkoBrowserTitle('app.browserTitle'),
  ]
})
export class AppModule { }
